module Colours {

    export interface hsl {
        h: number;
        s: number;
        l: number;
    }

    export interface rgb {
        r: number;
        g: number;
        b: number;
    }

    // values returned are 0 -> 255
    export function hexToRgb(hex: string): rgb {
        const isOk = /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/.test(hex);
        if(!isOk) {
            console.warn("🌈 Failed to parse colour: " + hex);
            return { r: 0, g: 0, b: 0 };
        }
        const isSix = hex.length === 7;
        const r = parseInt(isSix ? `${hex[1]}${hex[2]}` : `${hex[1]}${hex[1]}`, 16);
        const g = parseInt(isSix ? `${hex[3]}${hex[4]}` : `${hex[2]}${hex[2]}`, 16);
        const b = parseInt(isSix ? `${hex[5]}${hex[6]}` : `${hex[3]}${hex[3]}`, 16);
        return { r, g, b };
    }

    function decimalToHex(i: number): string {
        const num = +i;
        return num > 255 || num < 0 ? "00" : num.toString(16).padStart(2, "0").toUpperCase();
    }

    export function rgbToHex(rgb: rgb): string {
        return `#${decimalToHex(rgb.r)}${decimalToHex(rgb.g)}${decimalToHex(rgb.b)}`;
    }

    // convert #888 to #888888 etc.
    export function fullHex(hex: string): string {
        const isOk = /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/.test(hex);
        if(!isOk) return hex;
        if(hex.length === 7) return hex;
        return `#${hex[1]}${hex[1]}${hex[2]}${hex[2]}${hex[3]}${hex[3]}`;
    }

    // values returned are 0 -> 1
    export function rgbToHsl(r: string|number, g?: number, b?: number): hsl {
        if(typeof r === "string") {
            const rgb = hexToRgb(r);
            r = rgb.r;
            g = rgb.g;
            b = rgb.b;
        }
        if(typeof b === "undefined" || typeof g === "undefined") {
            console.log(`Bail out - g:${g} b:${b}`);
            return { h: 0, s: 0, l: 0 };
        }
        r /= 255, g /= 255, b /= 255;
        const max = Math.max(r, g, b), min = Math.min(r, g, b);
        let h = 0;
        let s = 0;
        const l = (max + min) / 2;
        if(max == min){
            h = s = 0; // achromatic
        }
        else {
            const d = max - min;
            s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
            switch(max) {
                case r: h = (g - b) / d + (g < b ? 6 : 0); break;
                case g: h = (b - r) / d + 2; break;
                case b: h = (r - g) / d + 4; break;
            }
            h /= 6;
        }
        return { h, s, l };
    }

    // per luma coefficients in https://en.wikipedia.org/wiki/Rec._709 (should result in a number 0 -> 255)
    export const luminance = (rgb: rgb): number => 0.2126 * rgb.r + 0.7152 * rgb.g + 0.0722 * rgb.b;
        
    export function isDark(hex: string): boolean {
        return luminance(hexToRgb(hex)) < 140;
    }
}

export default Colours;
